import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PromptPayDialog } from "@/components/PromptPayDialog";
import { ArrowRight, Car, Ticket, Users, Wallet, Bus, Trash2, XCircle, QrCode } from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [rides, setRides] = useState([]);
  const [busBookings, setBusBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payBooking, setPayBooking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, r, bb] = await Promise.all([
        api.get("/my/bookings"), api.get("/my/rides"), api.get("/my/bus-bookings"),
      ]);
      setBookings(b.data); setRides(r.data); setBusBookings(bb.data);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cancelBooking = async (id) => {
    try { await api.post(`/bookings/${id}/cancel`); toast.success("Booking cancelled"); load(); }
    catch (e) { toast.error("Could not cancel"); }
  };
  const cancelRide = async (id) => {
    try { await api.post(`/rides/${id}/cancel`); toast.success("Ride cancelled"); load(); }
    catch (e) { toast.error("Could not cancel ride"); }
  };

  const activeBookings = bookings.filter((b) => b.status === "confirmed");
  const totalSpent = activeBookings.reduce((s, b) => s + (b.total_price || 0), 0);

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="glass p-5"><div className="flex items-center gap-3"><div className="w-11 h-11 rounded-xl grid place-items-center glass-solid" style={{ boxShadow: `0 0 20px ${color}33` }}><Icon size={20} style={{ color }} /></div><div><div className="font-display text-2xl font-semibold text-white">{value}</div><div className="text-xs text-muted-cosmic">{label}</div></div></div></Card>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-3xl font-semibold">Hello, {user?.name?.split(" ")[0]} 👋</h1>
      <p className="text-muted-cosmic mb-6">Your cosmic travel command center.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Ticket} label="Active bookings" value={activeBookings.length} color="#22D3EE" />
        <StatCard icon={Car} label="Rides published" value={rides.length} color="#7C3AED" />
        <StatCard icon={Bus} label="Bus tickets" value={busBookings.length} color="#F7C948" />
        <StatCard icon={Wallet} label="Spent (฿)" value={totalSpent.toFixed(0)} color="#34D399" />
      </div>

      <Tabs defaultValue="bookings">
        <TabsList className="bg-white/5">
          <TabsTrigger value="bookings" data-testid="dashboard-bookings-tab">My bookings</TabsTrigger>
          <TabsTrigger value="rides" data-testid="dashboard-rides-tab">My rides</TabsTrigger>
          <TabsTrigger value="bus" data-testid="dashboard-bus-tab">Bus tickets</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-4 space-y-3">
          {loading ? <Skeleton className="h-24 w-full bg-white/5" /> : bookings.length === 0 ? (
            <Card className="glass p-8 text-center text-muted-cosmic">No bookings yet. <Link to="/search" className="text-[#22D3EE]">Find a ride →</Link></Card>
          ) : bookings.map((b) => (
            <Card key={b.booking_id} className="glass p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10"><AvatarImage src={b.ride?.driver?.picture} /><AvatarFallback className="bg-[#3B82F6] text-white text-xs">{(b.ride?.driver?.name||"?").slice(0,2)}</AvatarFallback></Avatar>
                  <div>
                    <div className="flex items-center gap-2 font-display">{b.ride?.from_city} <ArrowRight size={14} className="text-[#7C3AED]" /> {b.ride?.to_city}</div>
                    <div className="text-xs text-muted-cosmic font-mono-cosmic">{b.ride?.date} · {b.ride?.time} · {b.seats} seat(s)</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-display text-[#FFF7D6]">{b.total_price} ฿</span>
                  {b.status === "confirmed" ? <Badge className="bg-[rgba(52,211,153,0.14)] text-[#6EE7B7] border border-[rgba(52,211,153,0.25)]">Confirmed</Badge> : b.status === "awaiting_payment" ? <Badge className="bg-[rgba(247,201,72,0.14)] text-[#FDE68A] border border-[rgba(247,201,72,0.3)]">Awaiting payment</Badge> : <Badge className="bg-[rgba(251,113,133,0.14)] text-[#FB7185] border border-[rgba(251,113,133,0.25)]">Cancelled</Badge>}
                  {b.status === "awaiting_payment" && b.promptpay_qr && <Button data-testid="dashboard-pay-now" size="sm" onClick={() => setPayBooking(b)} className="bg-[hsl(var(--primary))] text-white h-8 gap-1"><QrCode size={14} /> Pay now</Button>}
                  {b.ride?.driver && <Button size="sm" variant="ghost" onClick={() => navigate(`/profile/${b.ride.driver.user_id}`)} className="text-[#22D3EE]">Review</Button>}
                  {b.status !== "cancelled" && <Button data-testid="dashboard-cancel-booking" size="sm" variant="ghost" onClick={() => cancelBooking(b.booking_id)} className="text-[#FB7185] gap-1"><XCircle size={15} /> Cancel</Button>}
                </div>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="rides" className="mt-4 space-y-3">
          {loading ? <Skeleton className="h-24 w-full bg-white/5" /> : rides.length === 0 ? (
            <Card className="glass p-8 text-center text-muted-cosmic">You haven't published any rides. <Link to="/publish" className="text-[#22D3EE]">Publish one →</Link></Card>
          ) : rides.map((r) => (
            <Card key={r.ride_id} className="glass p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Link to={`/rides/${r.ride_id}`}><div className="flex items-center gap-2 font-display">{r.from_city} <ArrowRight size={14} className="text-[#7C3AED]" /> {r.to_city}</div><div className="text-xs text-muted-cosmic font-mono-cosmic">{r.date} · {r.time}</div></Link>
                <div className="flex items-center gap-3">
                  <Badge className="bg-[rgba(34,211,238,0.12)] text-[#A5F3FC] border border-[rgba(34,211,238,0.22)] gap-1"><Users size={11} /> {r.bookings?.length || 0} booked</Badge>
                  <span className="text-sm text-muted-cosmic">{r.seats_available}/{r.seats_total} left</span>
                  {r.status === "active" ? <Button data-testid="dashboard-cancel-ride" size="sm" variant="ghost" onClick={() => cancelRide(r.ride_id)} className="text-[#FB7185] gap-1"><Trash2 size={15} /> Cancel</Button> : <Badge className="bg-[rgba(251,113,133,0.14)] text-[#FB7185] border border-[rgba(251,113,133,0.25)]">Cancelled</Badge>}
                </div>
              </div>
              {r.bookings?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-2">
                  {r.bookings.map((bk) => (
                    <div key={bk.booking_id} className="flex items-center gap-1 text-xs text-secondary-cosmic"><Avatar className="h-6 w-6"><AvatarImage src={bk.passenger?.picture} /><AvatarFallback className="bg-[#3B82F6] text-white text-[10px]">{(bk.passenger?.name||"?").slice(0,2)}</AvatarFallback></Avatar>{bk.passenger?.name} ({bk.seats})</div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="bus" className="mt-4 space-y-3">
          {loading ? <Skeleton className="h-24 w-full bg-white/5" /> : busBookings.length === 0 ? (
            <Card className="glass p-8 text-center text-muted-cosmic">No bus tickets yet. <Link to="/bus" className="text-[#22D3EE]">Book a coach →</Link></Card>
          ) : busBookings.map((b) => (
            <Card key={b.booking_id} className="glass p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-xl grid place-items-center glass-solid"><Bus size={18} className="text-[#F7C948]" /></div><div><div className="font-display">{b.trip?.from_city} → {b.trip?.to_city}</div><div className="text-xs text-muted-cosmic font-mono-cosmic">{b.trip?.operator} · {b.trip?.date} · {b.trip?.time}</div></div></div>
              <div className="flex items-center gap-3"><span className="font-display text-[#FFF7D6]">{b.total_price} ฿</span><Badge className="bg-[rgba(52,211,153,0.14)] text-[#6EE7B7] border border-[rgba(52,211,153,0.25)]">{b.seats} seat(s)</Badge></div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
      <PromptPayDialog booking={payBooking} type={payBooking?.trip_id ? "bus" : "ride"} open={!!payBooking} onOpenChange={(o) => { if (!o) setPayBooking(null); }} onConfirmed={load} />
    </div>
  );
};

export default Dashboard;
